import { Link } from "react-router-dom";
import { initials } from "../../../helpers/helpers";
import ImageControl from "../../common/ImageControl";

/**
 * The company page header, built to the agreed design: a back link, the name
 * beside its monogram or logo, a one-line summary, and six facts.
 *
 * Written as its own block rather than by rearranging .top_block. The styles
 * in _style.scss are deep descendant selectors - .top_block .inner .text
 * .heading .sponsor_tag > img is what held that Active Sponsor star to
 * 1.2rem - so markup moved out of those paths silently loses its styling,
 * which is exactly how a full-size green star ended up across the hero. A
 * new structure gets new CSS of its own and borrows nothing.
 *
 * Every tile is omitted when we do not hold the fact, so an employer we know
 * little about gets a shorter row rather than a grid of blanks.
 */

const Icon = ({ d, muted }) => (
  <svg
    width="30"
    height="30"
    viewBox="0 0 24 24"
    fill="none"
    stroke={muted ? "#9AA3B2" : "#1355ff"}
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {d}
  </svg>
);

const PATHS = {
  doc: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M9 15h6" />
    </>
  ),
  roles: (
    <>
      <path d="M3 6h18" />
      <path d="M3 12h18" />
      <path d="M3 18h12" />
    </>
  ),
  shield: (
    <>
      <path d="M12 2 4 6v6c0 5 3.4 8.6 8 10 4.6-1.4 8-5 8-10V6z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18" />
    </>
  ),
  pin: (
    <>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  case: (
    <>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    </>
  ),
};

const RATING = {
  a_rated: "A-rated licence",
  b_rated: "B-rated licence",
  provisional: "Provisional licence",
};

export default function CompanyHero({ company, cos, totalJobs }) {
  const name = company?.title || "";
  const rating = RATING[company?.sponsor_rating];
  const overseas = company?.overseas_applicants === "yes";

  // The line under the name: only the parts we actually hold.
  const summary = [
    company?.company_location,
    rating,
    overseas ? "Accepts overseas applicants" : null,
  ].filter(Boolean);

  const roleCount = Number(cos?.roles) || 0;
  const tiles = [];

  if (cos?.label) {
    tiles.push({
      icon: "doc",
      value: `${cos.label} sponsored in 2025`,
      label: cos.suppressed > 0 ? "Certificates of Sponsorship, at least" : "Certificates of Sponsorship",
    });
  }
  if (roleCount > 0) {
    tiles.push({
      icon: "roles",
      value: `${roleCount} different ${roleCount === 1 ? "role" : "roles"}`,
      label: "Sponsored in 2025",
    });
  }
  if (rating) {
    tiles.push({
      icon: "shield",
      value: rating,
      label: company?.visa_support || "Sponsor licence",
    });
  }
  if (company?.overseas_applicants) {
    tiles.push({
      icon: "globe",
      value: "Overseas applicants",
      label: overseas ? "Accepted" : "Not accepted",
    });
  }
  if (company?.company_location) {
    tiles.push({
      icon: "pin",
      value: company.company_location,
      label: "Registered address",
    });
  }
  tiles.push(
    totalJobs > 0
      ? {
          icon: "case",
          value: `${totalJobs} live ${totalJobs === 1 ? "job" : "jobs"}`,
          label: "jobs",
        }
      : { icon: "case", value: "No live jobs", label: "alert", muted: true },
  );

  return (
    <div className="company_hero">
      <div className="contain">
        <Link to="/explore-companies/1" className="back">
          &larr;&nbsp; All companies
        </Link>

        <div className="panel">
          <div className="head">
            {company?.image ? (
              <div className="logo">
                <ImageControl
                  src={company.image}
                  folder="companies"
                  isThumb={true}
                />
              </div>
            ) : (
              <div className="monogram" aria-hidden="true">
                {initials(name)}
              </div>
            )}
            <div className="title">
              <h1>{name}</h1>
              {summary.length > 0 && <p>{summary.join("  ·  ")}</p>}
            </div>
          </div>

          <div className="facts">
            {tiles.map((t) => (
              <div className="fact" key={t.icon + t.value}>
                <Icon d={PATHS[t.icon]} muted={t.muted} />
                <p className={t.muted ? "value muted" : "value"}>{t.value}</p>
                {t.label === "alert" ? (
                  <p className="label">
                    <Link to="/job-alerts">Set an alert</Link>
                  </p>
                ) : t.label === "jobs" ? (
                  <p className="label">
                    <Link
                      to={`/open-jobs/1?searchKeyword=${encodeURIComponent(name)}`}
                    >
                      See them on the job board
                    </Link>
                  </p>
                ) : (
                  <p className="label">{t.label}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
